import { useCallback, useMemo, useState } from 'react';
import { Column } from '@tempmee/react';
import { type Control, useController, useWatch } from 'react-hook-form';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useAppSelector } from '~/hooks/useAppSelector';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { EmbeddedInviteList } from './EmbeddedInviteList';
import { type TInviteTab } from './EmbeddedInviteList/TabHeader';
import { InvitingSection, type IInvitedProfessional } from './InvitingSection';

interface IInviteFieldProps {
  control: Control<IShiftDetailsFormValues>;
  professionType: string;
  officeId: string;
  disabled?: boolean;
  /** Notifies the parent when the list has no professionals (drives the card dim). */
  onListEmptyChange?: (isEmpty: boolean) => void;
}

export const InviteField = ({
  control,
  professionType,
  officeId,
  disabled = false,
  onListEmptyChange,
}: IInviteFieldProps) => {
  const { field } = useController({ name: 'invite', control });
  const { field: inviteNamesField } = useController({ name: 'inviteNames', control });
  const [professionalToRemove, setProfessionalToRemove] = useState<string | null>(null);
  const [isAllEmpty, setIsAllEmpty] = useState(false);

  const inviteCategory = useWatch({ control, name: 'inviteCategory' });
  const selectedDates = useWatch({ control, name: 'dates' });
  const initialTabHint = useMemo<TInviteTab | undefined>(() => {
    if (inviteCategory === 'favorites') return 'favorites';
    if (inviteCategory === 'worked_with') return 'worked_with';
    if (inviteCategory === 'new_to_you') return 'new_to_you';
    return undefined;
  }, [inviteCategory]);

  const selectedChildOffice = useAppSelector((state: any) => state.user.selectedChildOffice);
  const effectiveOfficeId = officeId || selectedChildOffice?.id || '';

  const favoriteProfessionals = useAppSelector((state) => state.user.favoriteProfessionals ?? []);
  const recentProfessionals = useAppSelector((state) => state.user.recentProfessionals ?? []);
  const recommendedProfessionals = useAppSelector(
    (state) => state.user.recommendedProfessionals ?? [],
  );

  const resolveInvited = useCallback(
    (id: string): IInvitedProfessional => {
      const favorite = (favoriteProfessionals as any[]).find((p) => p?.id === id);
      if (favorite) {
        return {
          id,
          name:
            favorite.fullName ??
            favorite.full_name ??
            favorite.fullname ??
            inviteNamesField.value?.[id] ??
            '',
          type: 'favorite',
          photoUrl: favorite.profileImage ?? favorite.profile_image,
        };
      }
      const recent = (recentProfessionals as any[]).find(
        (p) => p?.id === id || p?.professional?.id === id,
      );
      if (recent) {
        const pro = recent.professional ?? recent;
        return {
          id,
          name: pro.fullName ?? pro.full_name ?? pro.fullname ?? inviteNamesField.value?.[id] ?? '',
          type: 'recent',
          photoUrl: pro.image ?? pro.profileImage ?? pro.profile_image,
        };
      }
      const recommended = (recommendedProfessionals as any[]).find((p) => p?.id === id);
      if (recommended) {
        return {
          id,
          name:
            recommended.fullName ??
            recommended.full_name ??
            recommended.fullname ??
            inviteNamesField.value?.[id] ??
            '',
          type: 'new',
          photoUrl: recommended.profileImage ?? recommended.profile_image,
        };
      }
      return {
        id,
        name: inviteNamesField.value?.[id] ?? '',
        type: 'new',
      };
    },
    [favoriteProfessionals, recentProfessionals, recommendedProfessionals, inviteNamesField.value],
  );

  const invited = useMemo<IInvitedProfessional[]>(
    () => (field.value ?? []).map(resolveInvited),
    [field.value, resolveInvited],
  );

  const handleRemove = useCallback((id: string) => {
    setProfessionalToRemove(id);
  }, []);

  const handleConfirmRemove = useCallback(() => {
    if (professionalToRemove) {
      field.onChange(field.value.filter((id: string) => id !== professionalToRemove));
      setProfessionalToRemove(null);
    }
  }, [field, professionalToRemove]);

  const removingName = professionalToRemove
    ? (invited.find((p) => p.id === professionalToRemove)?.name ?? '')
    : '';

  const handleAllEmptyChange = useCallback(
    (value: boolean) => {
      setIsAllEmpty(value);
      onListEmptyChange?.(value);
    },
    [onListEmptyChange],
  );

  return (
    <>
      <Column
        gap={3}
        data-testid="invite-field"
        data-cy="invite-field"
        style={{ margin: '0 0 0 24px' }}
      >
        <EmbeddedInviteList
          professionType={professionType}
          officeId={effectiveOfficeId}
          selectedDates={selectedDates}
          selectedIds={field.value ?? []}
          onSelectionChange={(ids) => {
            // Card-level pointer-events block clicks; this guards keyboard / programmatic
            // changes so no selection is captured while the field is locked.
            if (disabled) return;
            field.onChange(ids);
          }}
          onAllEmptyChange={handleAllEmptyChange}
          initialTabHint={initialTabHint}
        />

        {!isAllEmpty && <InvitingSection invited={invited} onRemove={handleRemove} />}
      </Column>

      {professionalToRemove && (
        <ConfirmPopup
          title="Remove Professional From Invite"
          description={
            <>
              Are you sure you would like to remove <strong>{removingName}</strong> from your
              request?
            </>
          }
          leftButtonText="Go Back"
          leftButtonAction={() => setProfessionalToRemove(null)}
          rightButtonText="Remove"
          rightButtonAction={handleConfirmRemove}
          closePopup={() => setProfessionalToRemove(null)}
          dataTestId="remove-professional-popup"
          descriptionStyle={{
            width: '85%',
            marginBottom: 32,
          }}
        />
      )}
    </>
  );
};
